import { ChangeDetectionStrategy, Component, inject, linkedSignal, viewChild } from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { SelectedContentAssetStore } from '../store/selected-content-asset.store';
import { injectParams } from 'ngxtension/inject-params';
import { ContentLibraryEnableService } from './content-library-enable.service';
import { EnableEntityComponent } from '@r1-compass-library/components/enable-entity/enable-entity.component';
import { EnableSaveGuardWarning } from '@r1-compass-library/guards/save-guards/enable.guard';

@Component({
    selector: 'r1-compass-app-content-library-enable',
    imports: [EnableEntityComponent],
    template: `
        <r1-compass-lib-enable-entity
            [currentEnableStatus]="currentEnableStatus()"
            [copyHeader]="'Enable for Play Use'"
            [entityType]="'Content Library'"
            (save)="onSave()"
            (toggleChange)="onToggleChange($event)">
            Toggle “Enabled” to allow plays to use this Content Asset or “Disabled” to prevent this Content Asset from
            being used. The Content Asset must not be in use by any active Plays to be disabled.
        </r1-compass-lib-enable-entity>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ContentLibraryEnableService],
})
export class ContentLibraryEnableComponent implements EnableSaveGuardWarning {
    readonly #store = inject(SelectedContentAssetStore);
    readonly #enableService = inject(ContentLibraryEnableService);

    protected currentEnableStatus = linkedSignal<boolean | undefined>(() => this.#store.asset()?.enabled);

    public enableComponentSpy = viewChild.required(EnableEntityComponent);

    private contentAssetId = injectParams<string | null>('assetId');

    constructor() {
        const contentAssetId = this.contentAssetId();
        if (contentAssetId && contentAssetId !== 'new' && this.#store.asset()?.id !== +contentAssetId) {
            this.#store.getAsset({ id: +contentAssetId });
        }
    }

    public onSave() {
        return this.#enableService.save(this.currentEnableStatus());
    }

    protected async onToggleChange(event: MatSlideToggleChange) {
        const contentAsset = this.#store.asset();
        if (!contentAsset) {
            return;
        }

        const outcome = event.checked
            ? await this.#enableService.enable()
            : await this.#enableService.disable(contentAsset);

        if (outcome.status === 'success') {
            this.currentEnableStatus.set(outcome.value.shouldSetEnabled);
            event.source.checked = outcome.value.shouldSetEnabled;
        }
    }
}
